
import { Component, ChangeDetectionStrategy, inject, signal, OnInit, input, effect } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule, DatePipe } from '@angular/common'; 
import { Title } from '@angular/platform-browser'; 

import { DataService } from '../../services/data.service';
import { PrescriptionService } from '../../services/prescription.service';
import { Prescription, ClinicSettings } from '../../models/index';

@Component({
  selector: 'app-prescription-form',
  standalone: true,
  imports: [CommonModule, DatePipe],
  templateUrl: './prescription-form.component.html',
  styleUrls: ['./prescription-form.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrescriptionFormComponent implements OnInit {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private dataService = inject(DataService);
  private prescriptionService = inject(PrescriptionService);
  private titleService: Title = inject(Title);
  
  prescriptionIdInput = input<string | null>(null, { alias: 'prescriptionId' });
  prescription = signal<any | null>(null);
  effectiveClinicSettings = signal<ClinicSettings | null>(null);
  signatureUrl = signal<string | null>(null);
  
  isLoading = signal(true);
  
  // Date displayed on the prescription document
  documentDate = new Date();

  constructor() {
    effect(() => {
        const id = this.prescriptionIdInput();
        if (id) {
            this.loadData(id);
        }
    });
  }

  ngOnInit(): void {
    const routeId = this.route.snapshot.paramMap.get('prescriptionId');
    if (routeId) {
      this.loadData(routeId);
    } else if (this.prescriptionIdInput()) {
      this.loadData(this.prescriptionIdInput()!);
    } else {
      this.isLoading.set(false);
    }
  }

  async loadData(id: string): Promise<void> {
    this.isLoading.set(true);
    try {
      const prescData = await this.dataService.getPrescriptionForPrinting(id);
      this.prescription.set(prescData);

      let clinicData: ClinicSettings | null = null;

      if (prescData && prescData.providerDetails) {
          const pd = prescData.providerDetails;
          clinicData = {
              licenseNumber: pd.license_number || '',
              professionType: pd.profession_type || 'TRADITIONAL',
              addressText: pd.address || '',
              headerText: pd.header_text || 'ใบสั่งจ่ายสมุนไพรควบคุม (กัญชา)',
              certificateLogoUrl: pd.logo_url,
              certificateBgUrl: pd.bg_url,
              doctorSignatureUrl: pd.signature_url,
              useCustomTemplate: pd.use_custom_template || false,
              certificateHeaderStyle: pd.certificate_header_style || 'NONE',
              watermarkText: pd.watermark_text || '',
              operatingHours: '', 
              enableTelemedAutoApprove: false 
          };
      } else {
           clinicData = await this.dataService.getClinicSettings();
      }
      
      this.effectiveClinicSettings.set(clinicData);

      // --- Determine Final Signature ---
      let finalSig: string | null = null;

      if (prescData && prescData.doctor_signature_snapshot) {
          finalSig = prescData.doctor_signature_snapshot;
      }
      
      if (!finalSig) {
          finalSig = clinicData?.doctorSignatureUrl || null;
      }

      this.signatureUrl.set(finalSig);
      // ------------------------------------------------

      if (prescData) {
        // --- DATE FIX: Strictly use reviewedAt (Approval Date) ---
        const validDateStr = prescData.reviewedAt || prescData.createdAt;
        if (validDateStr) {
            this.documentDate = new Date(validDateStr);
        }
        // --------------------------------------------------------

        const patientName = prescData.customer?.fullName || 'Patient';
        const dateForTitle = this.documentDate.toISOString().split('T')[0];
        this.titleService.setTitle(`Medical_Cert_${patientName}_${dateForTitle}`);
        
        if (!this.prescriptionIdInput()) {
             setTimeout(() => window.print(), 800);
        }
      }
    } catch (e) {
      console.error("Failed to load prescription data:", e);
    } finally {
      this.isLoading.set(false);
    }
  }

  get customerAge(): number | null {
    const birthDateStr = this.prescription()?.customer?.birthDate;
    if (!birthDateStr) return null;
    const birthDate = new Date(birthDateStr);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
  }

  get doctorLicenseNumber(): string {
    return this.effectiveClinicSettings()?.licenseNumber || '---';
  }

  getThaiMonth(monthIndex: number): string {
      const months = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];
      return months[monthIndex] || "";
  }
}
